const WORKFLOW_HOST = "workflow.grupocenze.com.br:38137";
const WORKFLOW_REST_PREFIX = `https://${WORKFLOW_HOST}/rest/`;

export const PORTAL_ORIGINS = [
  "https://cenze-links.vercel.app",
  "http://localhost:3000",
];

let bridgeInitialized = false;
let requestCounter = 0;
const pendingRequests = new Map();

export function isPortalEmbedded() {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
}

export function isWorkflowUrl(url) {
  return String(url).includes(WORKFLOW_HOST);
}

export function getPortalOrigin() {
  if (document.referrer) {
    try {
      const refOrigin = new URL(document.referrer).origin;
      if (PORTAL_ORIGINS.includes(refOrigin)) {
        return refOrigin;
      }
    } catch {
      // ignore invalid referrer
    }
  }
  return PORTAL_ORIGINS[0];
}

export function extractWorkflowPath(url) {
  const parsed = new URL(url, window.location.href);
  const restMarker = "/rest/";
  const restIdx = parsed.pathname.indexOf(restMarker);
  const path =
    restIdx >= 0
      ? parsed.pathname.slice(restIdx + restMarker.length)
      : String(url).replace(WORKFLOW_REST_PREFIX, "").split("?")[0];
  const query = parsed.search || "";
  return `${path.replace(/^\//, "")}${query}`;
}

export function rewriteWorkflowUrl(url) {
  if (!isPortalEmbedded() || !isWorkflowUrl(url)) {
    return url;
  }
  const portalOrigin = getPortalOrigin();
  return `${portalOrigin}/api/protheus/${extractWorkflowPath(url)}`;
}

export function initPortalEmbedBridge() {
  if (bridgeInitialized) {
    return;
  }
  bridgeInitialized = true;

  window.addEventListener("message", (event) => {
    if (!PORTAL_ORIGINS.includes(event.origin)) {
      return;
    }
    const data = event.data || {};
    if (data.type === "PORTAL_API_RESPONSE") {
      const pending = pendingRequests.get(data.id);
      if (!pending) {
        return;
      }
      pendingRequests.delete(data.id);
      pending.resolve(data);
    }
    if (data.type === "PORTAL_EMBED_PONG") {
      window.dispatchEvent(
        new CustomEvent("portal-embed-ready", { detail: data }),
      );
    }
  });

  if (isPortalEmbedded()) {
    window.parent.postMessage({ type: "PORTAL_EMBED_PING" }, getPortalOrigin());
  }
}

function headersToObject(headers) {
  if (!headers) {
    return {};
  }
  if (headers instanceof Headers) {
    const result = {};
    headers.forEach((value, key) => {
      result[key] = value;
    });
    return result;
  }
  return { ...headers };
}

export function portalBridgeFetch(url, options = {}) {
  initPortalEmbedBridge();
  const id = `req_${++requestCounter}_${Date.now()}`;
  const portalOrigin = getPortalOrigin();
  const path = extractWorkflowPath(url);

  return new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      pendingRequests.delete(id);
      reject(new Error("Portal API bridge timeout"));
    }, 15000);

    pendingRequests.set(id, {
      resolve: (data) => {
        clearTimeout(timeoutId);
        const response = {
          ok: Boolean(data.ok),
          status: Number(data.status || 0),
          headers: new Headers(data.responseHeaders || {}),
          url: data.url || url,
          json: async () => {
            if (typeof data.body === "string") {
              return JSON.parse(data.body);
            }
            return data.body;
          },
          text: async () =>
            typeof data.body === "string" ? data.body : JSON.stringify(data.body),
        };
        if (!data.ok) {
          const error = new Error(
            typeof data.body === "string" ? data.body : "API request failed",
          );
          error.response = response;
          error.status = response.status;
          reject(error);
          return;
        }
        resolve(response);
      },
    });

    window.parent.postMessage(
      {
        type: "PORTAL_API_REQUEST",
        id,
        method: (options.method || "GET").toUpperCase(),
        path,
        headers: headersToObject(options.headers),
        body:
          typeof options.body === "string"
            ? options.body
            : options.body
              ? JSON.stringify(options.body)
              : null,
      },
      portalOrigin,
    );
  });
}

export async function portalAwareFetch(url, options = {}) {
  initPortalEmbedBridge();
  if (!isPortalEmbedded() || !isWorkflowUrl(url)) {
    return fetch(url, options);
  }

  const proxyUrl = rewriteWorkflowUrl(url);
  try {
    const response = await fetch(proxyUrl, options);
    if (response.status !== 0) {
      return response;
    }
  } catch {
    // fall through to postMessage bridge
  }

  return portalBridgeFetch(url, options);
}
